<template>
  <div class="table" :class="[tableStatusClass, { selected: selected }]" @click="$emit('click')">
    <a class="table-container">
      <div class="table-status"></div>
      <div class="table-name">
        <div class="name">{{ $t('restaurantSale.table') }}</div>
        <div class="no">{{ table.table_no }}</div>
        <div class="countdown">
          <span v-if="table.status_id === 3" class="countdown-timer">
            <span>{{ minutesLeft }}</span>
          </span>
          <span v-else class="status-text">
            {{ getStatusText() }}
          </span>
        </div>
      </div>
      <div class="table-info-row">
        <div class="table-info-col">
          <div class="table-info-container">
            <span class="icon">
              <span v-show="table.status_id != 1">🕐</span>
            </span>
            <span class="text">{{ table.start_time }}</span>
          </div>
        </div>
        <div class="table-info-col">
          <div class="table-info-container">
            <span class="icon">
              <span v-show="table.status_id != 1 && hasTimeLimit">⏳</span>
            </span>
            <span class="text">{{ endTime }}</span>
          </div>
        </div>
      </div>
      <div class="table-info-row">
        <div class="table-info-col">
          <div class="table-info-container">
            <span class="icon">
              <span v-show="table.status_id != 1">💰</span>
            </span>
            <span class="text"
              >{{ defaultCurrency }}
              {{ table.invoice_amount | format_number }}</span
            >
          </div>
        </div>
        <div class="table-info-col">
          <div class="table-info-container">
            <span class="icon">
              <span v-show="table.status_id != 1">💵</span>
            </span>
            <span class="text">{{ table.invoice_status }}</span>
          </div>
        </div>
      </div>
    </a>
  </div>
</template>

<script>
export default {
  name: 'TableCard',
  props: {
    table: {
      type: Object,
      required: true
    },
    selected: {
      type: Boolean,
      default: false
    },
    defaultCurrency: {
      type: String,
      default: ''
    }
  },
  filters: {
    format_number(value) {
      if (value) {
        return `${parseFloat(value).toLocaleString()}`
      }
      return 0
    }
  },
  computed: {
    hasTimeLimit() {
      return this.getMaxServiceLength() !== null;
    },
    tableStatusClass() {
      // Check for over time alert first (assuming status_id 4 or a new field)
      if (this.table.status_id == 4 || this.table.over_time_alert) {
        return 'over-time-alert'
      }

      // Existing status checks
      if (this.table.status_id == 1) {
        return 'available'
      }
      if (this.table.status_id == 2) {
        return 'disabled'
      }
      if (this.table.status_id == 3) {
        // Additional check for over time within in-use status
        if (this.hasTimeLimit && this.isTableOverTime()) {
          return 'over-time-alert'
        }
        if (this.hasTimeLimit && this.isTableNearTime()) {
          return 'near-time-alert'
        }
        return 'in-use'
      }

      return 'available' // fallback
    },
    minutesLeft() {
      if (this.table.status_id !== 3) {
        return ''
      }

      // Unlimited time case
      if (!this.hasTimeLimit) {
        return 'Unlimited'
      }

      const timeLeft = this.calculateTimeLeft()

      if (timeLeft <= 0) {
        return 'OVERTIME'
      }

      const totalMinutes = Math.ceil(timeLeft / (60 * 1000)) // Round up minutes
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60

      if (hours > 0) {
        return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
      }

      return `${minutes}m`
    },
    endTime() {
      if (!this.table.start_time) return ''
      if (!this.hasTimeLimit) return 'Unlimited'
      const startTime = this.parseTimeString(this.table.start_time)
      if (!startTime) return ''
      const end = new Date(startTime.getTime() + this.getMaxServiceLength() * 60 * 1000)
      return end.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
    }
  },
  methods: {
    getStatusText() {
      // Return appropriate text for non-active tables
      switch (this.table.status_id) {
        case 1:
          return 'Available'
        case 2:
          return 'Disabled'
        case 4:
          return 'Over Time'
        default:
          return ''
      }
    },
    getMaxServiceLength() {
      // Find the maximum service_length from invoice_detail items
      if (!this.table.invoice_detail || !Array.isArray(this.table.invoice_detail)) {
        return null // No time limit
      }

      const serviceLengths = this.table.invoice_detail
        .map(item => item.service_length)
        .filter(length => length !== null && length !== undefined)

      if (serviceLengths.length === 0) {
        return null // No time limit
      }

      return Math.max(...serviceLengths)
    },
    isTableOverTime() {
      // Check if table has been occupied for more than the max service length
      if (!this.table.start_time || !this.hasTimeLimit) {
        return false
      }

      const currentTime = new Date()

      // Parse the start_time format "11:17 AM"
      const startTime = this.parseTimeString(this.table.start_time)
      if (!startTime) {
        return false
      }

      const timeLimitInMs = this.getMaxServiceLength() * 60 * 1000

      return currentTime - startTime > timeLimitInMs
    },
    isTableNearTime() {
      const timeLeft = this.calculateTimeLeft()
      return timeLeft > 0 && timeLeft <= 15 * 60 * 1000
    },
    calculateTimeLeft() {
      if (!this.table.start_time || !this.hasTimeLimit) {
        return 0
      }

      const currentTime = new Date()
      const startTime = this.parseTimeString(this.table.start_time)

      if (!startTime) {
        return 0
      }

      const timeLimitInMs = this.getMaxServiceLength() * 60 * 1000
      const elapsedTime = currentTime - startTime
      const timeLeft = timeLimitInMs - elapsedTime

      return timeLeft > 0 ? timeLeft : 0
    },
    parseTimeString(timeStr) {
      try {
        // Parse time string like "11:17 AM"
        const today = new Date()
        const [time, period] = timeStr.trim().split(' ')
        const [hours, minutes] = time.split(':')

        let hour24 = parseInt(hours)
        const min = parseInt(minutes)

        // Convert to 24-hour format
        if (period.toUpperCase() === 'PM' && hour24 !== 12) {
          hour24 += 12
        } else if (period.toUpperCase() === 'AM' && hour24 === 12) {
          hour24 = 0
        }

        // Create date object with today's date but specified time
        const parsedTime = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
          hour24,
          min,
          0
        )

        // Handle case where start_time might be from yesterday (past midnight scenario)
        const now = new Date()
        if (parsedTime > now) {
          // If parsed time is in the future, assume it was from yesterday
          parsedTime.setDate(parsedTime.getDate() - 1)
        }

        return parsedTime
      } catch (error) {
        console.warn('Failed to parse time string:', timeStr, error)
        return null
      }
    },
  },

  mounted() {
    // Update countdown every second for active tables with time limit
    if (this.table.status_id === 3 && this.hasTimeLimit) {
      this.countdownInterval = setInterval(() => {
        this.$forceUpdate() // Force re-render to update countdown
      }, 1000)
    }
  },

  beforeDestroy() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
    }
  },

  data() {
    return {
      countdownInterval: null
    }
  }
}
</script>

<style scoped lang="scss">
/* Add styles for countdown timer */
.countdown {
  font-weight: bold;
}

.countdown-timer {
  color: #007bff;
  /* Blue color for active countdown */
  font-size: 1.1em;
}

.countdown-timer.overtime {
  color: #dc3545;
  /* Red color for overtime */
  animation: blink 1s infinite;
}

.status-text {
  color: #6c757d;
  /* Gray color for status text */
  font-size: 0.9em;
}

@keyframes blink {

  0%,
  50% {
    opacity: 1;
  }

  51%,
  100% {
    opacity: 0.3;
  }
}

/* Additional styling for over-time-alert tables */
.table.over-time-alert .countdown-timer {
  color: #dc3545;
  animation: blink 1s infinite;
}

/* 15-minute pre-warning */
.table.near-time-alert .countdown-timer {
  color: #fd7e14;
  animation: blink 1s infinite;
}

// .pos-counter .pos-counter-body .pos-counter-content .table-row .table {
//   width: 16.66%;
//   flex: 0 0 16.66%;

//   @media (max-width: 1250px) {
//     width: 20%;
//     flex: 0 0 20%;
//   }

//   @media (max-width: 1199.98px) {
//     width: 25%;
//     flex: 0 0 25%;
//   }

//   @media (max-width: 767.98px) {
//     width: 33.33%;
//     flex: 0 0 33.33%;
//   }

//   @media (max-width: 575.98px) {
//     width: 50%;
//     flex: 0 0 50%;
//   }
// }


.pos-counter .pos-counter-body .pos-counter-content .table-row .table .table-container .table-name {
  padding: 5px;
}

.pos-counter .pos-counter-body .pos-counter-content .table-row .table .table-container .table-name .no {
  font-size: 28px;
}
</style>